import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Menu, X, Code, Zap, Database, ExternalLink } from 'lucide-react';
import emailjs from '@emailjs/browser';
// --- CONFIGURAÇÕES DE SEGURANÇA ---
// Idealmente, use um arquivo .env para estas chaves
const EMAILJS_CONFIG = {
  SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || "YOUR_SERVICE_ID",
  TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID",
  PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "YOUR_PUBLIC_KEY",
};

// Função de máscara para telefone (fixo e celular BR)
const formatPhone = (value) => {
  if (!value) return value;
  const onlyNums = value.replace(/\D/g, '');

  if (onlyNums.length <= 2) {
    return onlyNums.replace(/^(\d{0,2})/, '($1');
  }
  if (onlyNums.length <= 6) {
    return onlyNums.replace(/^(\d{2})(\d{0,4})/, '($1) $2');
  }
  if (onlyNums.length <= 10) {
    return onlyNums.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  }
  // Celular (11 dígitos) ou mais
  return onlyNums
    .slice(0, 11)
    .replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

// --- COMPONENTES REUTILIZÁVEIS ---
const NavLink = ({ id, label, onClick, isActive }) => (
  <a
    href={`#${id}`}
    onClick={(e) => onClick(e, id)}
    className={`text-xs font-bold transition-colors uppercase tracking-widest ${
      isActive ? 'text-cyan-400' : 'text-slate-400 hover:text-cyan-400'
    }`}
  >
    <span className="text-cyan-500/50 mr-1.5">{/* // */}</span>{label}
  </a>
);
const ServiceCard = ({ icon, title, description, index }) => (
  <div
    className="reveal opacity-0 translate-y-12 transition-all duration-1000 group p-10 bg-white/[0.02] border border-white/5 rounded-[32px] hover:border-cyan-500/40"
    style={{ transitionDelay: `${index * 150}ms` }}
  >
    <div className="w-14 h-14 bg-cyan-500/10 rounded-2xl flex items-center justify-center mb-8 text-cyan-400 group-hover:bg-cyan-500 group-hover:text-slate-950 transition-all duration-500 shadow-xl">
      {icon}
    </div>
    <h4 className="text-xl font-black text-white mb-4 uppercase tracking-tight italic">{title}</h4>
    <p className="text-slate-400 leading-relaxed text-sm font-medium">{description}</p>
  </div>
);
const FormInput = ({ type = "text", name, placeholder, value, onChange, required = true }) => (
  <input
    required={required}
    type={type}
    name={name}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:border-cyan-500 transition-all placeholder:text-slate-600 text-xs font-bold tracking-widest text-white"
  />
);
// --- COMPONENTE PRINCIPAL ---
export default function WenCoreWebsite() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [activeProjectIndex, setActiveProjectIndex] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
 
  const formRef = useRef();
  // Dados memorizados para evitar recriação em cada render
  const projects = useMemo(() => [
    {
      title: "IntegraSync",
      category: "Plataforma SaaS / ERP",
      description: "Um ecossistema completo para gestão de Marketplaces, integrando com sua conta em diversos players de mercado.",
      url: "https://integrasyncpilot-pro.vercel.app",
      image: "/integra.jpg",
      tags: ["React", "Node.js", "Cloud"]
    },
    {
      title: "FleetProSystem",
      category: "Plataforma SaaS / ERP",
      description: "Um sistema completo para gestão de frotas de caminhões. Controle viagens, custos e manutenções em uma única plataforma.",
      url: "https://fleet-manager-hub.vercel.app/",
      image: "/fleetprosystem.jpeg",
      tags: ["React", "Node.js", "Cloud"]
    }
  ], []);
  const services = useMemo(() => [
    { icon: <Code className="w-7 h-7" />, title: "Desenvolvimento Sob Encomenda", description: "Softwares personalizados para atender às necessidades específicas do seu negócio" },
    { icon: <Zap className="w-7 h-7" />, title: "Integração de Sistemas", description: "Conectamos seus sistemas via API para otimizar processos e fluxos de dados" },
    { icon: <Database className="w-7 h-7" />, title: "Tratamento de Dados", description: "Análise, organização e processamento inteligente de dados empresariais" }
  ], []);
  // Lógica de Scroll e Animações (Intersection Observer)
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);
  // Carrossel Automático de Projetos
  useEffect(() => {
    if (projects.length <= 1) return;
    const interval = setInterval(() => {
      setActiveProjectIndex((prev) => (prev >= projects.length - 1 ? 0 : prev + 1));
    }, 10000);
    return () => clearInterval(interval);
  }, [projects.length]);
  const scrollToSection = useCallback((e, id) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const element = document.getElementById(id);
   
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
     
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
      // Atualiza URL sem recarregar
      window.history.pushState(null, null, id === 'home' ? ' ' : `#${id}`);
    }
  }, []);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatPhone(value);
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    try {
      await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        formRef.current,
        EMAILJS_CONFIG.PUBLIC_KEY
      );
      alert('Solicitação enviada com sucesso!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Erro EmailJS:', err);
      alert('Falha ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSending(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans selection:bg-cyan-500/30 overflow-x-hidden">
     
      {/* HEADER */}
      <header className={`fixed w-full z-[100] transition-all duration-500 ${scrolled ? 'py-4 bg-slate-950/80 backdrop-blur-xl border-b border-white/5 shadow-2xl' : 'py-6 bg-transparent'}`}>
        <nav className="container mx-auto px-6 flex items-center justify-between">
          <div onClick={(e) => scrollToSection(e, 'home')} className="flex items-center space-x-3 group cursor-pointer active:scale-95 transition-transform">
            <div className="relative w-10 h-10">
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500 to-blue-600 rounded-xl rotate-3 shadow-[0_0_15px_rgba(6,182,212,0.4)] group-hover:rotate-6 transition-transform"></div>
              <div className="absolute inset-0.5 bg-slate-950 rounded-[10px] flex items-center justify-center italic font-black text-cyan-400 text-xl">W</div>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-white uppercase">WenCore<span className="text-cyan-500">.</span></h1>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold leading-none">Tech</p>
            </div>
          </div>
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-10">
            <NavLink id="home" label="Início" onClick={scrollToSection} />
            <NavLink id="servicos" label="Serviços" onClick={scrollToSection} />
            <NavLink id="projetos" label="Projetos" onClick={scrollToSection} />
            <NavLink id="sobre" label="Sobre" onClick={scrollToSection} />
            <button
              onClick={(e) => scrollToSection(e, 'contact')}
              className="px-6 py-2.5 bg-cyan-500 text-slate-950 text-xs font-black rounded-full hover:bg-white transition-all transform active:scale-95 uppercase tracking-wider"
            >
              Contato
            </button>
          </div>
          <button className="md:hidden text-cyan-400 p-2 z-[110]" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </nav>
        {/* Mobile Menu */}
        <div className={`fixed inset-0 h-screen bg-slate-950 transition-all duration-500 md:hidden z-[90] flex flex-col items-center justify-center space-y-8 ${isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`}>
          {['home', 'servicos', 'projetos', 'sobre'].map((id) => (
            <a key={id} href={`#${id}`} onClick={(e) => scrollToSection(e, id)} className="text-2xl font-black italic text-white uppercase tracking-tighter">
              {id === 'home' ? 'Início' : id}
            </a>
          ))}
          <button onClick={(e) => scrollToSection(e, 'contact')} className="px-8 py-3 bg-cyan-500 text-slate-950 font-black rounded-xl uppercase">Contato</button>
        </div>
      </header>
      {/* HERO SECTION */}
      <section id="home" className="relative pt-44 pb-32 px-6 overflow-hidden">
        <div className="container mx-auto text-center relative z-10 reveal opacity-0 translate-y-12 transition-all duration-1000">
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm uppercase text-[10px] font-black tracking-widest text-cyan-400">
            <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></span>
            Líderes em Inovação
          </div>
          <h2 className="text-5xl md:text-8xl font-black mb-8 leading-[0.9] tracking-tighter text-white uppercase italic">
            Transforme com <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 bg-clip-text text-transparent">tecnologia de ponta.</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-12 font-medium">
            Desenvolvimento de software customizado e soluções inteligentes para escalar seu impacto digital.
          </p>
        </div>
      </section>
      {/* PROJETOS (CASOS DE SUCESSO) */}
      <section id="projetos" className="py-24 px-6 bg-[#03081a]">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 reveal opacity-0 translate-y-12 transition-all duration-1000">
            <div>
              <h3 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter mb-4">Casos de Sucesso</h3>
              <p className="text-slate-500 font-bold uppercase text-xs tracking-[0.2em]">{/* // Experiências que geram valor */}</p>
            </div>
            <div className="flex gap-2 mb-4">
              {projects.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-500 ${activeProjectIndex === idx ? 'w-12 bg-cyan-500' : 'w-4 bg-white/10'}`}
                />
              ))}
            </div>
          </div>
          <div className="relative h-[650px] md:h-[500px]">
            {projects.map((project, index) => (
              <article
                key={index}
                className={`absolute inset-0 transition-all duration-1000 transform ${
                  activeProjectIndex === index ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95 pointer-events-none'
                }`}
              >
                <div className="relative group overflow-hidden bg-white/[0.02] border border-white/10 rounded-[40px] p-8 md:p-12 transition-all hover:border-cyan-500/50 shadow-2xl h-full">
                  <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                      <span className="inline-block px-4 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-[10px] font-black uppercase tracking-widest mb-6 italic">
                        {project.category}
                      </span>
                      <h4 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase tracking-tighter italic leading-none">{project.title}</h4>
                      <p className="text-slate-400 text-lg mb-8 leading-relaxed font-medium">{project.description}</p>
                      <div className="flex flex-wrap gap-3 mb-10">
                        {project.tags.map(tag => (
                          <span key={tag} className="px-4 py-1.5 bg-white/5 rounded-full text-[10px] font-bold text-slate-500 border border-white/5">#{tag}</span>
                        ))}
                      </div>
                      <a href={project.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-white text-slate-950 font-black rounded-2xl hover:bg-cyan-500 transition-all uppercase text-xs tracking-widest group/btn">
                        Ver Projeto <ExternalLink size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    </div>
                    <div className="relative hidden lg:block overflow-hidden rounded-2xl border border-white/10 aspect-video">
                      <img src={project.image} alt={project.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      {/* SERVICES */}
      <section id="servicos" className="py-24 px-6 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-20 reveal opacity-0 translate-y-12 transition-all duration-1000">
            <h3 className="text-3xl md:text-5xl font-black text-white mb-6 italic uppercase tracking-tighter">Nossos Serviços</h3>
            <div className="h-1 w-12 bg-cyan-500 mx-auto rounded-full shadow-[0_0_10px_rgba(6,182,212,1)]"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>
      {/* CONTACT SECTION */}
      <section id="contact" className="py-24 px-6 bg-slate-950">
        <div className="container mx-auto max-w-5xl reveal opacity-0 translate-y-12 transition-all duration-1000">
          <div className="bg-gradient-to-br from-white/[0.04] to-transparent border border-white/10 rounded-[40px] overflow-hidden">
            <div className="grid md:grid-cols-5">
              <div className="md:col-span-2 bg-cyan-600 p-12 flex flex-col justify-between">
                <div>
                  <h3 className="text-4xl font-black text-slate-950 mb-6 leading-[0.9] uppercase italic tracking-tighter">Vamos iniciar sua jornada?</h3>
                  <p className="text-slate-900 font-bold opacity-80 uppercase text-[10px] tracking-widest leading-relaxed">Resposta em até 24 horas úteis.</p>
                </div>
                <div className="text-slate-950 font-black italic tracking-widest text-xs pt-6">{/* // WENCORE TECH */}</div>
              </div>
              <div className="md:col-span-3 p-12">
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormInput name="name" placeholder="NOME" value={formData.name} onChange={handleInputChange} />
                    <FormInput name="email" type="email" placeholder="E-MAIL" value={formData.email} onChange={handleInputChange} />
                  </div>
                  <FormInput 
                    name="phone" 
                    type="tel" 
                    placeholder="TELEFONE" 
                    value={formData.phone} 
                    onChange={handlePhoneChange}  // ← Máscara aqui
                  />
                  <textarea
                    required
                    name="message"
                    placeholder="COMO PODEMOS AJUDAR?"
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:border-cyan-500 transition-all resize-none placeholder:text-slate-600 text-xs font-bold tracking-widest text-white"
                  />
                  <button
                    type="submit"
                    disabled={isSending}
                    className="w-full py-5 bg-cyan-500 text-slate-950 font-black rounded-2xl hover:bg-white transition-all transform active:scale-[0.98] uppercase tracking-[0.2em] text-xs shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSending ? 'Processando...' : 'Enviar Solicitação'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* FOOTER */}
      <footer className="py-12 px-6 border-t border-white/5 text-center bg-[#020617]">
        <div className="container mx-auto">
          <div className="text-white font-black text-xl mb-4 italic uppercase tracking-tighter underline decoration-cyan-500 decoration-2">WenCore Tech<span className="text-cyan-500">.</span></div>
          <p className="text-slate-600 text-[10px] font-black uppercase tracking-[0.4em] mb-4">
            © {new Date().getFullYear()} Wencore Tech {/* // */} Todos os direitos reservados
          </p>
          <div className="inline-block px-4 py-1 rounded-full border border-white/5 text-[9px] text-slate-500 font-bold uppercase tracking-widest bg-white/5">CNPJ: XX.XXX.XXX/XXXX-XX</div>
        </div>
      </footer>
    </div>
  );
}
